"use client";

import { useEffect, useState } from "react";
import axios from "../api/axios";
import { toast } from "react-toastify";
import FileHistory from "./FileHistory";
import {
  Search,
  Download,
  Eye,
  FileText,
  Users,
  Calendar,
  SlidersHorizontal,
  X,
  File,
  Folder,
  Clock,
  User,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  ToggleLeft,
  ToggleRight,
  Trash,
  Pencil,
  History,
} from "lucide-react";

function Files() {
  const [user, setUser] = useState(null);
  const [teams, setTeams] = useState([]);
  const [allFiles, setAllFiles] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [fileTypes, setFileTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("all");
  const [selectedFileType, setSelectedFileType] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("date");
  const [expandedFolders, setExpandedFolders] = useState({});
  const [visibleFiles, setVisibleFiles] = useState({});
  const [viewMode, setViewMode] = useState("fileType"); // "fileType" or "teams"

  const INITIAL_FILES_COUNT = 5;

  const [editFile, setEditFile] = useState(null); // The file you are editing
  const [newFilename, setNewFilename] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [historyFile, setHistoryFile] = useState(null);

  const openEditModal = (file) => {
    setEditFile(file);
    setNewFilename(file.filename);
    setNewFile(null);
  };

  const handleUpdate = async () => {
    if (isUpdating) return;
    if (!newFilename.trim()) {
      toast.error("Please enter a filename");
      return;
    }
    setIsUpdating(true);
    const formData = new FormData();
    formData.append("filename", newFilename);
    if (newFile) {
      formData.append("new_file", newFile);
    }

    try {
      const response = await axios.post(
        `/api/files/${editFile.id}?_method=PUT`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast.success("File updated successfully");
      setAllFiles((previous) =>
        previous.map((file) => file.id === editFile.id ? {
          ...file,
          filename: response.data.file.name,
          filepath: response.data.file.path,
        } : file)
      );

      setEditFile(null);
    } catch (error) {
      console.error(error);
      toast.error("Update failed");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm("Are you sure you want to delete this file?")) {
      return;
    }

    try {
      await axios.delete(`/api/files/${fileId}`);

      // Optional: Show success notification
      toast.success("File deleted successfully");

      // Remove the file from local state (React side)
      setAllFiles((previous) => previous.filter((file) => file.id !== fileId));
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete file");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await axios.get("/api/user", { withCredentials: true });
        setUser(userRes.data);

        const fileTypesRes = await axios.get("/api/file-type");
        setFileTypes(fileTypesRes.data);

        const teamsRes = await axios.get("/api/teams_with_files");
        setTeams(teamsRes.data);

        const files = teamsRes.data.flatMap((team) =>
          (team.files || []).map((file) => ({
            ...file,
            teamName: team.name,
            teamId: team.id,
          }))
        );
        setAllFiles(files);

        // Initialize visible files count
        const initialVisibleFiles = {};
        if (viewMode === "teams") {
          teamsRes.data.forEach((team) => {
            initialVisibleFiles[team.id] = INITIAL_FILES_COUNT;
          });
        } else {
          fileTypesRes.data.forEach((type) => {
            initialVisibleFiles[type.id] = INITIAL_FILES_COUNT;
          });
        }
        setVisibleFiles(initialVisibleFiles);
      } catch (err) {
        console.error("Error fetching data:", err);
        toast.error("Failed to load data");
      }
    };

    fetchData();
  }, []);

  // console.log(teams);

  useEffect(() => {
    filterAndGroupData();
  }, [
    searchTerm,
    selectedTeam,
    selectedFileType,
    dateFilter,
    sortBy,
    teams,
    fileTypes,
    allFiles,
    viewMode,
  ]);

  const filterAndGroupData = () => {
    let filteredFiles = [...allFiles];

    // Apply filters
    if (searchTerm) {
      filteredFiles = filteredFiles.filter(
        (file) =>
          file.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          file.teamName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          file.user?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedTeam !== "all") {
      filteredFiles = filteredFiles.filter(
        (file) => file.teamId.toString() === selectedTeam
      );
    }

    if (selectedFileType !== "all") {
      filteredFiles = filteredFiles.filter((file) => {
        if (file.file_type_id === null || file.file_type_id === undefined) {
          return false;
        }
        return file.file_type_id.toString() === selectedFileType;
      });
    }

    if (dateFilter !== "all") {
      const now = new Date();
      const filterDate = new Date();

      switch (dateFilter) {
        case "today":
          filterDate.setHours(0, 0, 0, 0);
          filteredFiles = filteredFiles.filter(
            (file) =>
              file.uploaded_at && new Date(file.uploaded_at) >= filterDate
          );
          break;
        case "week":
          filterDate.setDate(now.getDate() - 7);
          filteredFiles = filteredFiles.filter(
            (file) =>
              file.uploaded_at && new Date(file.uploaded_at) >= filterDate
          );
          break;
        case "month":
          filterDate.setMonth(now.getMonth() - 1);
          filteredFiles = filteredFiles.filter(
            (file) =>
              file.uploaded_at && new Date(file.uploaded_at) >= filterDate
          );
          break;
        case "year":
          filterDate.setFullYear(now.getFullYear() - 1);
          filteredFiles = filteredFiles.filter(
            (file) =>
              file.uploaded_at && new Date(file.uploaded_at) >= filterDate
          );
          break;
      }
    }

    // Sort files
    filteredFiles.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return (a.filename || "").localeCompare(b.filename || "");
        case "size":
          return (b.size || 0) - (a.size || 0);
        case "type":
          return getFileTypeName(a.file_type_id).localeCompare(
            getFileTypeName(b.file_type_id)
          );
        case "date":
        default:
          const dateA = a.uploaded_at ? new Date(a.uploaded_at) : new Date(0);
          const dateB = b.uploaded_at ? new Date(b.uploaded_at) : new Date(0);
          return dateB - dateA;
      }
    });

    // Group by view mode
    let groupedData = [];

    if (viewMode === "teams") {
      // Group by teams
      const teamGroups = {};
      filteredFiles.forEach((file) => {
        const teamId = file.teamId;
        if (!teamGroups[teamId]) {
          const team = teams.find((t) => t.id === teamId);
          teamGroups[teamId] = {
            id: teamId,
            name: team?.name || "Unknown Team",
            description: team?.description,
            files: [],
          };
        }
        teamGroups[teamId].files.push(file);
      });
      groupedData = Object.values(teamGroups);
    } else {
      // Group by file types
      const typeGroups = {};
      filteredFiles.forEach((file) => {
        const typeId = file.file_type_id || "no-type";
        const typeName = getFileTypeName(file.file_type_id);

        if (!typeGroups[typeId]) {
          typeGroups[typeId] = {
            id: typeId,
            name: typeName,
            description: `Files of type: ${typeName}`,
            files: [],
          };
        }
        typeGroups[typeId].files.push(file);
      });
      groupedData = Object.values(typeGroups);
    }

    setFilteredData(groupedData);
  };

  const getFileTypeName = (id) => {
    if (id === null || id === undefined) {
      return "No Type Selected";
    }
    const type = fileTypes.find((t) => t.id.toString() === id.toString());
    return type ? type.name : `Unknown (ID: ${id})`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  const toggleFolderExpansion = (folderId) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  const showMoreFiles = (folderId) => {
    setVisibleFiles((prev) => ({
      ...prev,
      [folderId]: (prev[folderId] || INITIAL_FILES_COUNT) + INITIAL_FILES_COUNT,
    }));
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedTeam("all");
    setSelectedFileType("all");
    setDateFilter("all");
  };

  const activeFiltersCount = [
    searchTerm,
    selectedTeam !== "all" ? selectedTeam : null,
    selectedFileType !== "all" ? selectedFileType : null,
    dateFilter !== "all" ? dateFilter : null,
  ].filter(Boolean).length;

  const totalFiles = allFiles.length;
  const totalFilteredFiles = filteredData.reduce(
    (sum, folder) => sum + (folder.files?.length || 0),
    0
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-3 bg-orange-100 rounded-xl">
              <FileText size={24} className="text-orange-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Files & Documents
              </h1>
              <p className="text-gray-600">
                Browse and manage all your files organized by{" "}
                {viewMode === "teams" ? "teams" : "file types"}
              </p>
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-700">
                  View by:
                </span>
                <button
                  onClick={() =>
                    setViewMode(viewMode === "teams" ? "fileType" : "teams")
                  }
                  className="flex items-center space-x-2 bg-orange-100 hover:bg-orange-200 text-orange-700 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  {viewMode === "teams" ? (
                    <ToggleRight size={20} />
                  ) : (
                    <ToggleLeft size={20} />
                  )}
                  <span>
                    {viewMode === "teams"
                      ? "Switch to File Types"
                      : "Switch to Teams"}
                  </span>
                </button>
              </div>
              <div className="text-sm text-gray-600">
                Currently viewing by:{" "}
                <span className="font-medium text-orange-600">
                  {viewMode === "teams" ? "Teams" : "File Types"}
                </span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-orange-100 rounded-xl">
                  <FileText size={20} className="text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {totalFiles}
                  </p>
                  <p className="text-sm text-gray-600">Total Files</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-green-100 rounded-xl">
                  <Users size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {teams.length}
                  </p>
                  <p className="text-sm text-gray-600">Active Teams</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Folder size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {fileTypes.length}
                  </p>
                  <p className="text-sm text-gray-600">File Types</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <Calendar size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {
                      allFiles.filter(
                        (file) =>
                          file.uploaded_at &&
                          new Date(file.uploaded_at) >
                            new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                      ).length
                    }
                  </p>
                  <p className="text-sm text-gray-600">This Week</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search
                  size={20}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search files, teams, or users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                showFilters || activeFiltersCount > 0
                  ? "bg-orange-100 text-orange-700 border border-orange-200"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <SlidersHorizontal size={20} />
              <span>Filters</span>
              {activeFiltersCount > 0 && (
                <span className="bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team
                  </label>
                  <select
                    value={selectedTeam}
                    onChange={(e) => setSelectedTeam(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="all">All Teams</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id.toString()}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    File Type
                  </label>
                  <select
                    value={selectedFileType}
                    onChange={(e) => setSelectedFileType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="all">All Types</option>
                    {fileTypes.map((type) => (
                      <option key={type.id} value={type.id.toString()}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Date
                  </label>
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="year">This Year</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sort By
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="date">Upload Date</option>
                    <option value="name">File Name</option>
                    <option value="size">File Size</option>
                    <option value="type">File Type</option>
                  </select>
                </div>
              </div>

              {activeFiltersCount > 0 && (
                <div className="mt-4 flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    Showing {totalFilteredFiles} of {totalFiles} files across{" "}
                    {filteredData.length}{" "}
                    {viewMode === "teams" ? "teams" : "file types"}
                  </span>
                  <button
                    onClick={clearFilters}
                    className="flex items-center space-x-1 text-orange-600 hover:text-orange-700 text-sm font-medium"
                  >
                    <X size={16} />
                    <span>Clear Filters</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Folders */}
        {filteredData.length > 0 ? (
          <div className="space-y-4">
            {filteredData.map((folder) => {
              const isExpanded = expandedFolders[folder.id];
              const folderFiles = folder.files || [];
              const visibleCount =
                visibleFiles[folder.id] || INITIAL_FILES_COUNT;
              const filesToShow = folderFiles.slice(0, visibleCount);
              const hasMoreFiles = folderFiles.length > visibleCount;

              return (
                <div
                  key={folder.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  {/* Folder Header */}
                  <div
                    className="p-6 cursor-pointer hover:bg-orange-50 transition-colors border-b border-gray-100"
                    onClick={() => toggleFolderExpansion(folder.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          {isExpanded ? (
                            <ChevronDown
                              size={20}
                              className="text-orange-600"
                            />
                          ) : (
                            <ChevronRight
                              size={20}
                              className="text-orange-600"
                            />
                          )}
                          {isExpanded ? (
                            <FolderOpen size={24} className="text-orange-600" />
                          ) : (
                            <Folder size={24} className="text-orange-600" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            {folder.name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {folderFiles.length} file
                            {folderFiles.length !== 1 ? "s" : ""}
                            {folder.description && ` • ${folder.description}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
                          {folderFiles.length} files
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Folder Files */}
                  {isExpanded && (
                    <div className="p-6 pt-0">
                      {folderFiles.length > 0 ? (
                        <div className="space-y-3">
                          {filesToShow.map((file) => (
                            <div
                              key={file.id}
                              className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-center space-x-4 flex-1 min-w-0">
                                <div className="p-2 bg-orange-100 rounded-lg">
                                  <File size={20} className="text-orange-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4
                                    className="font-medium text-gray-900 truncate"
                                    title={file.filename}
                                  >
                                    {file.filename || "Unnamed File"}
                                  </h4>
                                  <span className="inline-block mt-1 text-xs font-semibold bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                    Current Version
                                  </span>
                                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 mt-1">
                                    <span className="flex items-center space-x-1">
                                      <FileText size={14} />
                                      <span>
                                        {getFileTypeName(file.file_type_id)}
                                      </span>
                                    </span>
                                    <span className="flex items-center space-x-1">
                                      <User size={14} />
                                      <span>
                                        {file.user?.name || "Unknown"}
                                      </span>
                                    </span>
                                    {viewMode === "fileType" && (
                                      <span className="flex items-center space-x-1">
                                        <Users size={14} />
                                        <span>{file.teamName}</span>
                                      </span>
                                    )}
                                    <span className="flex items-center space-x-1">
                                      <Clock size={14} />
                                      <span>
                                        {file.uploaded_at
                                          ? new Date(
                                              file.uploaded_at
                                            ).toLocaleDateString()
                                          : "Unknown Date"}
                                      </span>
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center gap-2 xl:shrink-0">
                                <button
                                  onClick={() => setHistoryFile(file)}
                                  className="flex items-center space-x-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded-lg text-sm font-medium"
                                >
                                  <History size={14} />
                                  <span>View History</span>
                                </button>
                                <a
                                  href={`https://kspdata.in/storage/${file.filepath}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center space-x-1 bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded-lg text-sm font-medium transition-colors"
                                >
                                  <Eye size={14} />
                                  <span>View</span>
                                </a>
                                <a
                                  href={`https://kspdata.in/storage/${file.filepath}`}
                                  download
                                  className="flex items-center space-x-1 bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1 rounded-lg text-sm font-medium transition-colors"
                                >
                                  <Download size={14} />
                                  <span>Download</span>
                                </a>
                              {(user?.role_id === 1 || user?.role_id === 2) && (
                                <>
                                  <button
                                    onClick={() => handleDelete(file.id)}
                                    className="flex items-center gap-1 bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded-lg text-sm font-medium"
                                  >
                                    <Trash size={14} />
                                    <span>Delete</span>
                                  </button>
                                </>
                              )}

                              {user && Number(user.id) === Number(file.user_id) && (
                                <>
                                  <button
                                    onClick={() => openEditModal(file)}
                                    className="flex items-center gap-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 px-3 py-1 rounded-lg text-sm font-medium"
                                  >
                                    <Pencil size={14} />
                                    <span>Edit</span>
                                  </button>
                                </>
                              )}
                              </div>
                            </div>
                          ))}

                          {/* Show More Button */}
                          {hasMoreFiles && (
                            <div className="text-center pt-4">
                              <button
                                onClick={() => showMoreFiles(folder.id)}
                                className="bg-orange-100 hover:bg-orange-200 text-orange-700 px-6 py-2 rounded-lg font-medium transition-colors"
                              >
                                Show{" "}
                                {Math.min(
                                  INITIAL_FILES_COUNT,
                                  folderFiles.length - visibleCount
                                )}{" "}
                                more files
                                <span className="ml-1 text-sm">
                                  ({folderFiles.length - visibleCount}{" "}
                                  remaining)
                                </span>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <File
                            size={32}
                            className="mx-auto text-gray-300 mb-2"
                          />
                          <p className="text-gray-500">
                            {searchTerm ||
                            selectedFileType !== "all" ||
                            dateFilter !== "all"
                              ? "No files match the current filters"
                              : `No files in this ${
                                  viewMode === "teams" ? "team" : "file type"
                                }`}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <Search size={24} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No files found
            </h3>
            <p className="text-gray-500">
              Try adjusting your search criteria or filters.
            </p>
          </div>
        )}
      </div>
      {historyFile && (
        <FileHistory file={historyFile} onClose={() => setHistoryFile(null)} />
      )}
      {editFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div role="dialog" aria-modal="true" aria-labelledby="edit-file-title"
            className="bg-white p-6 rounded-lg shadow-lg w-96 space-y-4">
            <h3 id="edit-file-title" className="text-lg font-bold">Edit File</h3>
            <label className="block text-sm text-gray-700">
              Filename
              <input type="text" value={newFilename} disabled={isUpdating}
                onChange={(e) => setNewFilename(e.target.value)}
                className="w-full border rounded px-3 py-2 mt-1" />
            </label>
            <label className="block text-sm text-gray-700">
              Replacement file (optional)
              <input type="file" disabled={isUpdating}
                onChange={(e) => setNewFile(e.target.files[0])}
                className="w-full border rounded px-3 py-2 mt-1" />
            </label>
            <p className="text-sm text-gray-500">Previous versions stay saved and can be opened from View History.</p>
            <div className="flex justify-end space-x-2 pt-4">
              <button onClick={() => setEditFile(null)} disabled={isUpdating}
                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50">Cancel</button>
              <button onClick={handleUpdate} disabled={isUpdating}
                className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50">
                {isUpdating ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Files;
